import { create } from "zustand"
import { persist } from "zustand/middleware"
import type { User } from "@/types/auth"

interface AuthState {
  user: User | null
  isAuthenticated: boolean
  /** Só em memória: ao recarregar a página, o cookie HttpOnly de refresh gera um novo (api.ts). */
  accessToken: string | null
  /**
   * Refresh token que versões antigas do app deixaram no localStorage (ADR-019). Fica só em memória e vai uma
   * única vez na primeira renovação, que grava o cookie HttpOnly; depois some.
   */
  legacyRefreshToken: string | null
  setAuth: (user: User, accessToken?: string) => void
  clearLegacyRefreshToken: () => void
  logout: () => void
}

/** Formato salvo pela versão 0, que guardava o refresh token no localStorage. */
type PersistedV0 = { user?: User | null; isAuthenticated?: boolean; refreshToken?: string | null }

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      user: null,
      isAuthenticated: false,
      accessToken: null,
      legacyRefreshToken: null,
      setAuth: (user, accessToken) =>
        set((state) => ({
          user,
          isAuthenticated: true,
          accessToken: accessToken !== undefined ? accessToken : state.accessToken,
        })),
      clearLegacyRefreshToken: () => set({ legacyRefreshToken: null }),
      logout: () => set({ user: null, isAuthenticated: false, accessToken: null, legacyRefreshToken: null }),
    }),
    {
      name: "fincontrol-auth",
      version: 1,
      // Nenhum token vai para o localStorage: o access token fica em memória e o refresh token só no cookie
      // HttpOnly, fora do alcance de JavaScript (e de um XSS)
      partialize: (state) => ({ user: state.user, isAuthenticated: state.isAuthenticated }),
      // A versão 0 guardava o refresh token aqui. Ele passa para a memória e sai do localStorage na hora,
      // porque o persist regrava o estado migrado já sem ele.
      migrate: (persisted, version) => {
        const old = (persisted ?? {}) as PersistedV0
        return {
          user: old.user ?? null,
          isAuthenticated: old.isAuthenticated ?? false,
          legacyRefreshToken: version < 1 ? old.refreshToken ?? null : null,
        } as unknown as AuthState
      },
    }
  )
)
